import { Button, Col, Grid, Icon } from 'gu-ui-kit'
import React, { useEffect, useMemo } from 'react'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import Connected from 'modules/Services/NetworksList/subcomponents/Items/Connected'
import Unlinked from 'modules/Services/NetworksList/subcomponents/Items/Unlinked'
import ServicesListSkeleton from './subcomponents/Placeholder'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import useFiltersManager from 'utils/hooks/useFiltersManager'
import config from 'modules/Services/NetworksList/filters'
import FilterPanel from 'components/Filters/FilterPanel'
import StoriesPreview from 'modules/Stories'
import { PANEL_NAVIGATE } from 'modules/Tickets/types'
import { FETCH_ADVERTISING_TYPES, FETCH_NETWORKS_DATA, SET_FILTERS } from 'modules/Services/types'
import PageHeader from 'components/PageHeader'
import styles from 'modules/Services/NetworksList/services-list.module.scss'
import EmptyResults from 'components/EmptyResults'
import useTotallyEmptyList from 'utils/useTotallyEmptyList'
import { useParams } from 'react-router-dom'
import GridSpinner from 'components/UI/GridSpinner'

const NetworksList = () => {
  const { t } = useTranslation()
  const breakpoint = useBreakpoint()
  const dispatch = useDispatch()
  const { loading, networks, filters, loaded, advertisingTypes } = useSelector(
    (state) => state['services/list'],
  )
  const { i18n } = useParams()
  const filterManager = useFiltersManager({
    filters: filters,
    config: config(),
    /*additionalFilterState: {
      ...filters
    },*/
    setAction: SET_FILTERS,
    localStorageKey: 'services/networks-list',
  })

  const notHidden = useMemo(() => networks.filter((network) => !network.is_hidden), [networks])

  const networksConnected = useMemo(
    () =>
      notHidden
        .filter((network) => network.has_account)
        .sort((a, b) => {
          if (a > b) {
            return 1
          }

          if (a < b) {
            return -1
          }

          return 0
        }),
    [notHidden],
  )

  const networksDisconnected = useMemo(
    () =>
      notHidden
        .filter((network) => !network.has_account)
        .sort((a, b) => {
          if (a > b) {
            return 1
          }

          if (a < b) {
            return -1
          }

          return 0
        }),
    [notHidden],
  )

  useEffect(() => {
    dispatch({
      type: FETCH_ADVERTISING_TYPES,
      payload: { params: { lang: i18n } },
    })
  }, [])

  useEffect(() => {
    if (!advertisingTypes.length) return

    filterManager.updateFilterConfigurationByKey({
      key: 'advertising_type_ids',
      val: {
        options: advertisingTypes.map(({ name, id }) => ({
          value: id,
          label: name,
        })),
      },
    })
  }, [advertisingTypes])

  useEffect(() => {
    if (!filterManager.filters) {
      return
    }

    const { query = '', ...filters } = filterManager.filters
    const queryParam = {}

    if (query?.length > 0) {
      queryParam.query = query
    }

    dispatch({
      type: FETCH_NETWORKS_DATA,
      payload: { params: { ...filters, ...queryParam } },
    })
  }, [filterManager.filters])

  const [, firstLoad] = useTotallyEmptyList(
    filterManager.params,
    networksConnected,
    loaded,
  )

  return (
    <>
      <Helmet defer={false}>
        <title>{t('common::nav_menu.advertisement_networks')}</title>
      </Helmet>

      {firstLoad ? (
        <GridSpinner />
      ) : (
        <Grid
          direction={'column'}
          gap={32}
          className={'layout-tile'}
        >
          <Grid
            wrap={false}
            justifyContent={breakpoint.md ? 'between' : 'start'}
            gap={32}
            direction={breakpoint.md ? 'row' : 'column-reverse'}
          >
            <Col
              flexGrow={1}
              flexShrink={1}
              className={styles['head__left-panel']}
            >
              <Grid direction={'column'}>
                <PageHeader className={'margin-bottom-24'}>{t('services::heading')}</PageHeader>

                <FilterPanel
                  filterManager={filterManager}
                  className={styles['filter-panel']}
                />
              </Grid>
            </Col>
            <Grid
              fluid={!breakpoint.fb}
              className={styles['head__right-panel']}
            >
              <StoriesPreview className={styles['list__stories']} />
            </Grid>
          </Grid>
          {loading ? (
            <ServicesListSkeleton />
          ) : networksConnected.length === 0 && networksDisconnected.length === 0 ? (
            <EmptyResults
              icon={
                <Icon
                  name={'gramophone'}
                  color={'Interface-Onsurface'}
                />
              }
              actionButton={
                <Button
                  variant={'primary'}
                  size={'l'}
                  startIcon={<Icon name={'plus'} />}
                  onClick={() =>
                    dispatch({
                      type: PANEL_NAVIGATE,
                      payload: {
                        route: 'new_ticket',
                        params: { id: undefined, title: t('services::add_new') },
                      },
                    })
                  }
                >
                  {t('services::add_new')}
                </Button>
              }
            />
          ) : (
            <>
              <Grid
                gap={16}
                className={styles['list__container']}
                cols={breakpoint.xxl ? 4 : breakpoint.lg ? 3 : breakpoint.md ? 2 : 1}
              >
                {networksConnected.map((network, i) => (
                  <Col
                    className={styles['list__col']}
                    key={'connected-' + i}
                  >
                    <Connected
                      {...network}
                      key={'connected-' + network.type}
                    />
                  </Col>
                ))}
                {networksDisconnected.map((network, i) => (
                  <Col
                    className={styles['list__col']}
                    key={'unlinked-' + i}
                  >
                    <Unlinked
                      {...network}
                      key={'unlinked-' + network.type}
                    />
                  </Col>
                ))}
              </Grid>
              <Grid justifyContent={'center'}>
                <Button
                  variant={'ghost'}
                  onClick={() =>
                    dispatch({
                      type: PANEL_NAVIGATE,
                      payload: {
                        route: 'new_ticket',
                        params: { id: undefined, title: t('services::add_new') },
                      },
                    })
                  }
                >
                  {t('services::add_new')}
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      )}
    </>
  )
}

export default NetworksList
